/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Rodrigo Cuadra
 🏢 Company: Ring2All LLC
 📄 File: hooks\useEmailTemplateList.ts
 🗓️ Created: 2026-05-02
 🧩 Description:
    Implementation of the email-templates module.
────────────────────────────────────────────────────────────────*/

import { useCallback } from 'react';
import { useDataGrid, type DataGridFetchResult } from '@/hooks/useDataGrid';
import { useNotifications } from '@/contexts/NotificationContext';
import { useI18n } from '@/hooks/useI18n';
import { getEmailTemplates } from '../api/emailTemplatesApi';
import { useEmailTemplateColumns } from './useEmailTemplateColumns';

export function useEmailTemplateList() {
    const { showError } = useNotifications();
    const { t } = useI18n();
    const { availableColumns } = useEmailTemplateColumns();

    const fetchTemplatesData = useCallback(
        async (params: {
            page: number;
            limit: number;
            sortBy: string;
            sortOrder: 'asc' | 'desc';
            search?: string;
        }) => {
            try {
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                const response: any = await getEmailTemplates(params);
                return {
                    data: response.data || [],
                    pagination: response.pagination ? {
                        page: response.pagination.page,
                        limit: response.pagination.limit,
                        total: response.pagination.total,
                        totalPages: response.pagination.pages
                    } : {
                        page: params.page,
                        limit: params.limit,
                        total: (response as any).length || 0,
                        totalPages: 1
                    }
                } as DataGridFetchResult;
            } catch (error) {
                showError(t('emailTemplates.messages.loadError'));
                throw error;
            }
        },
        [showError, t],
    );

    return useDataGrid({
        fetchData: fetchTemplatesData,
        initialColumns: availableColumns,
        initialSortBy: 'name',
        initialSortOrder: 'asc',
        initialLimit: 10,
        moduleName: 'emailTemplates',
    });
}
